import { ListItem, Typography } from '@material-ui/core';
import { useParams } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { useStyles } from '../../styles/styles';
import StyledCard from '../../components/cards/StyledCard';
import StyledCardHeader, { HeaderButtonIcon } from '../../components/cards/StyledCardHeader';
import StyledCardContent from '../../components/cards/StyledCardContent';
import { NameValueRowList } from '../../components/cards/CardRows/NameValueRowList';
import { VMPaginatedGrid } from '../../components/main/VMPaginatedGrid';
import { gridColumn } from '../../utils/gridUtils';
import { GridRowParams } from '@material-ui/data-grid';
import ActionsMenu from 'components/other/ActionsMenu';
import { drawerStore } from 'state/drawerStore';
import { FormInputTypes } from 'utils/constants';
import './DLMStyle.css';
import { useEffect } from 'react';
import { Api } from 'api';
import { usePaginatedProvider } from 'state/paginatedProviderStore';
import { useProvider } from 'state/providerStore';

const dayMap = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const getNowHHMM = () => {
  const now = new Date();
  return now.toTimeString().slice(0, 5);
};

const createDLMPeriodPayload = (rawValues: any) => {
  return {
    behaviourId: rawValues?.behaviourId,
    fromTime: rawValues.from ?? getNowHHMM(),
    toTime: rawValues.to ?? getNowHHMM(),
    specificDate: rawValues?.date,
    repeatRule: rawValues?.repeat,
    weekdays: rawValues?.weekDays?.map((d) => dayMap[d]) ?? [],
  };
};

const DLMDetailsOverview = observer(() => {
  const { dlmid } = useParams<{ dlmid: string }>();

  const strategyProvider = useProvider(() => Api.charge.getDLMStrategy(dlmid));
  const periodsProvider = usePaginatedProvider(undefined, undefined, false);
  const behaviourProvider = useProvider(() => Api.charge.listDLMBehaviours());

  const classes = useStyles();

  useEffect(() => {
    periodsProvider.setRequest((offset, sortBy, sortOrder) =>
      Api.charge.listDLMPeriods({
        skip: offset,
        take: 20,
        sortBy,
        sortOrder,
        strategyId: dlmid,
      }),
    );
    periodsProvider.setSort('fromTime', 'ASC');
  }, [dlmid]);

  const getBehaviourOptions = () =>
    behaviourProvider.data?.map((b) => ({
      value: b.id,
      label: b.name,
    }));

  const getDetailsRows = () => [
    { name: 'name', label: 'Name' },
    { name: 'description', label: 'Description' },
    { name: 'status', label: 'Status' },
  ];

  const dlmStrategyDetailsDrawerInputs = [
    { name: 'name', label: 'Name', type: FormInputTypes.textField },
    { name: 'description', label: 'Description', type: FormInputTypes.textField },
    {
      name: 'status',
      label: 'Status',
      type: FormInputTypes.select,
      options: ['Active', 'Inactive'],
    },
  ];

  const dlmDrawerInputs = [
    {
      name: 'behaviourId',
      label: 'Behaviour',
      type: FormInputTypes.select,
      options: getBehaviourOptions(),
    },
    {
      name: 'timeRange',
      label: 'Time Range',
      type: FormInputTypes.timeRange,
    },
    {
      name: 'date',
      label: 'Date',
      type: FormInputTypes.datePicker,
    },
    {
      name: 'repeat',
      label: 'Repeats',
      type: FormInputTypes.selectRepeatInterval,
      options: ['None', 'Daily', 'Weekly', 'Monthly'],
    },
  ];

  const handleAdd = () => {
    drawerStore.openDrawer({
      label: 'Add Strategy Period',
      inputs: dlmDrawerInputs,
      clickCallback: () => {
        const rawValues = drawerStore.values;
        const payload = createDLMPeriodPayload(rawValues);

        Api.charge.createDLMPeriodForStrategy(dlmid, payload).then(periodsProvider.reloadResource);
      },
    });
    drawerStore.setValues({});
  };

  const handleEdit = (row: any) => {
    drawerStore.openDrawer({
      label: 'Edit Strategy Period',
      inputs: dlmDrawerInputs,
      clickCallback: () => {
        const rawValues = drawerStore.values;
        const payload = createDLMPeriodPayload(rawValues);
        Api.charge.updateDLMPeriod(row.id, payload).then(periodsProvider.reloadResource);
      },
    });

    drawerStore.setValues({
      behaviourId: row.behaviourId,
      date: row.specificDate ? new Date(row.specificDate) : null,
      from: row.fromTime?.substring(0, 5),
      to: row.toTime?.substring(0, 5),
      repeat: row.repeatRule,
      weekDays:
        row.weekdays?.map((d) => ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].indexOf(d)) ??
        [],
    });
  };

  const handleRemove = (row: any) => {
    Api.charge.deleteDLMPeriod(row.id).then(periodsProvider.reloadResource);
  };

  const onClickEditDetails = () => {
    drawerStore.openDrawer({
      label: 'Edit Strategy Details',
      inputs: dlmStrategyDetailsDrawerInputs,
      clickCallback: () => {
        Api.charge
          .updateDLMStrategy(dlmid, drawerStore.values)
          .then(() => strategyProvider.reloadResource());
      },
    });
    drawerStore.setValues(strategyProvider.data);
  };

  const periodActions = (params: GridRowParams) => (
    <ActionsMenu>
      <ListItem button onClick={() => handleEdit(params.row)}>
        <Typography>Edit</Typography>
      </ListItem>
      <ListItem button onClick={() => handleRemove(params.row)}>
        <Typography>Remove</Typography>
      </ListItem>
    </ActionsMenu>
  );

  const strategyPeriodsColumns = [
    gridColumn('behaviourId', {
      headerName: 'Behaviour',
      flex: 2,
      minWidth: 150,
      renderCell: (params: GridRowParams) => (
        <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 'normal' }}>
          <div style={{ marginBottom: '0.375rem' }}>{params.row.behaviourName ?? '-'}</div>
          <div style={{ color: '#697483', fontSize: '0.875rem' }}>
            Max: {params.row.behaviour?.settings?.maxPower ?? 'N/A'} kW
          </div>
        </div>
      ),
    }),

    gridColumn('fromTime', {
      headerName: 'From',
      flex: 1,
      minWidth: 150,
      renderCell: (params: GridRowParams) => {
        const formatted = (params.row.fromTime ?? '').substring(0, 5).replace(':', '.');
        return <div>{formatted || '-'}</div>;
      },
    }),

    gridColumn('toTime', {
      headerName: 'To',
      flex: 1,
      minWidth: 150,
      renderCell: (params: GridRowParams) => {
        const formatted = (params.row.toTime ?? '').substring(0, 5).replace(':', '.');
        return <div>{formatted || '-'}</div>;
      },
    }),

    gridColumn('specificDate', {
      headerName: 'Date',
      flex: 2,
      minWidth: 150,
      renderCell: (params: GridRowParams) => {
        const dateStr = params.row.specificDate
          ? new Date(params.row.specificDate).toLocaleDateString('en-GB')
          : '-';
        return <div>{dateStr}</div>;
      },
    }),

    gridColumn('repeatRule', {
      headerName: 'Repeat',
      flex: 2,
      minWidth: 150,
      renderCell: (params: GridRowParams) => {
        const repeat = params.row.repeatRule ?? 'None';
        const days = params.row.weekdays?.join(', ') ?? '';
        return (
          <div>
            {repeat}
            {days && ` (${days})`}
          </div>
        );
      },
    }),

    gridColumn('', {
      renderCell: (params: GridRowParams) => periodActions(params),
      width: 60,
      align: 'left',
      headerAlign: 'left',
    }),
  ];

  return (
    <div style={{ width: '100%' }}>
      <div className={classes.cardRows}>
        <div className={classes.leftCardRow}>
          <StyledCard>
            <StyledCardHeader
              title="Details"
              onClick={onClickEditDetails}
              headerButtonIcon={HeaderButtonIcon.edit}
            />
            <StyledCardContent>
              <NameValueRowList rows={getDetailsRows()} values={strategyProvider.data} />
            </StyledCardContent>
          </StyledCard>
        </div>
        <div className={classes.rightCardRow}></div>
      </div>

      <div className="drawer-spacing-fix">
        <VMPaginatedGrid
          rows={periodsProvider.data}
          columns={strategyPeriodsColumns}
          label="Strategy Periods"
          count={periodsProvider.count}
          onClickAdd={handleAdd}
          onPageOffsetChange={periodsProvider.onPageChange}
          rowHeight={60}
        />
      </div>
    </div>
  );
});

export default DLMDetailsOverview;
